import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import { useEffect, useState } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

type Params<T extends FieldValues> = {
  name: Path<T>;
  computedValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  companyValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  dependencies?: Path<T>[];
};

export const useRecommendedValue = <T extends FieldValues>({
  companyValue,
  computedValue,
  name,
  dependencies,
}: Params<T>) => {
  const { setValue, getValues } = useFormContext<T>();

  const dependenciesValues = dependencies?.map((dependency) => getValues(dependency));

  const [prevDependenciesValues, setPrevDependenciesValues] = useState(dependenciesValues);

  const dependenciesHaveChanged = !isEqual(dependenciesValues, prevDependenciesValues);

  useEffect(() => {
    const recommendedValue = computedValue?.value || companyValue?.value;
    const hasRecommendedValue = !isNil(recommendedValue);
    const hasNoCurrentValue = isNil(getValues(name));

    if (
      (hasRecommendedValue && hasNoCurrentValue) ||
      (hasRecommendedValue && dependenciesHaveChanged)
    ) {
      setPrevDependenciesValues(dependenciesValues);
      setValue(name, recommendedValue);
    }
  }, [
    companyValue?.value,
    computedValue?.value,
    name,
    setValue,
    getValues,
    dependenciesHaveChanged,
    dependenciesValues,
  ]);
};
